import { Form, Image } from 'antd';
import dayjs from 'dayjs';
import React, { FC, useEffect } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { ModernDatePicker as DatePicker } from 'shared/ui/ModernDatePicker';
import { ModernInput as Input } from 'shared/ui/ModernInput';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { ModernSelect as Select } from 'shared/ui/ModernSelect';
import { datePickerValueUnder18, disableFutureDatesUnder18 } from 'shared/utils/helpers/disable18YearsDatePicker';
import { TextArea } from 'shared/ui/TextArea';
import { useGetUserVerificationDocumentsQuery, UserAccountType } from 'entities/User';
import { ModernPhoneInput as PhoneInput } from 'shared/ui/ModernPhoneInput';
import { PHONE_NUMBER_REGEX } from 'shared/utils/regex';

import PdfTemplate from 'shared/assets/images/PdfTemplate.png';
import { Drawer } from 'shared/ui/Drawer';
import { openPdf } from 'shared/utils/helpers/openPdf';
import { UpdateUser } from 'features/UpdateUser';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  accountType: UserAccountType;
  company?: Nullable<string>;
  identificationNumber?: Nullable<string>;
  dateOfBirth?: Nullable<string>;
  identificationDocIssuingAuthority?: Nullable<string>;
  identificationDocIssuingDate?: Nullable<string>;
  residenceRegistration?: Nullable<string>;
  signingIdentificator?: Nullable<string>;
  middleName?: Nullable<string>;
}

export const IdDetailsDrawer: FC<Props> = (props) => {
  const {
    isOpen,
    onClose,
    accountType,
    userId,
    email,
    firstName,
    lastName,
    phone,
    company,
    dateOfBirth,
    identificationDocIssuingAuthority,
    identificationDocIssuingDate,
    identificationNumber,
    residenceRegistration,
    signingIdentificator,
    middleName,
  } = props;
  const { data: documents } = useGetUserVerificationDocumentsQuery({ userId }, { skip: !isOpen });

  const { t } = useAppTranslation(['profile', 'common']);

  const [form] = Form.useForm();

  const formAccountType = Form.useWatch('accountType', form);

  const isBusinessAccountType = formAccountType === UserAccountType.BUSINESS;

  useEffect(() => {
    form.setFieldsValue({
      dateOfBirth: dateOfBirth ? dayjs(dateOfBirth) : undefined,
      firstName,
      lastName,
      email,
      phone,
      accountType,
      company,
      identificationNumber,
      residenceRegistration,
      signingIdentificator,
      identificationDocIssuingAuthority,
      identificationDocIssuingDate: identificationDocIssuingDate ? dayjs(identificationDocIssuingDate) : undefined,
      middleName,
    });
  }, [
    dateOfBirth,
    form,
    firstName,
    lastName,
    email,
    phone,
    accountType,
    company,
    identificationNumber,
    residenceRegistration,
    signingIdentificator,
    identificationDocIssuingAuthority,
    identificationDocIssuingDate,
    middleName,
  ]);

  return (
    <Drawer open={isOpen} onClose={onClose} width={1400} destroyOnClose>
      <div className="flex justify-between items-center py-4 px-8">
        <div className="font-semibold text-3xl font-tektur">{t('Verify data')}</div>
        <div className="text-primaryLight font-normal cursor-pointer" onClick={onClose}>
          {t('Close', { ns: 'common' })}
        </div>
      </div>

      <div className="flex gap-6 px-8 py-4">
        <div className="w-[55%] sticky top-0 max-h-[85vh] overflow-y-auto pr-2">
          <div className="flex flex-col gap-4">
            {documents?.length ? (
              documents.map((doc) => {
                if (!doc?.base64) return null;

                const isPdf = doc.base64.includes('application/pdf');

                return (
                  <div key={doc.documentType}>
                    {isPdf ? (
                      <div
                        onClick={() => {
                          openPdf(doc.base64);
                        }}
                        className="cursor-pointer border rounded-lg p-4 flex flex-col items-center hover:shadow"
                      >
                        <img src={PdfTemplate} className="w-[80px] h-[80px]" />
                        <span className="mt-2 text-sm text-gray-500">{t('Open PDF')}</span>
                      </div>
                    ) : (
                      <Image src={doc.base64} width="100%" className="rounded-lg object-cover" preview={{ scaleStep: 3 }} />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="flex justify-center text-xl text-primary">{t('No documents')}</div>
            )}
          </div>
        </div>
        <div className="w-[45%] max-h-[85vh] overflow-y-auto pl-2">
          <Form className="flex flex-col desktop:px-2" name="customerDetailsForm" form={form} layout="vertical">
            <div className="desktop:flex desktop:space-x-7">
              <Form.Item
                label={<span className="text-grey p-0">{t('Account type')}</span>}
                name="accountType"
                className="desktop:w-1/2"
                labelCol={{ style: { paddingBottom: 0 } }}
                rules={[{ required: true, message: t('Please, select account type') }]}
              >
                <Select
                  placeholder={t('Select account type')}
                  bordered
                  size="small"
                  options={[
                    { label: t('Personal'), value: UserAccountType.PERSONAL },
                    { label: t('Business'), value: UserAccountType.BUSINESS },
                  ]}
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prevValues, nextValues) => prevValues.accountType !== nextValues.accountType}>
                {({ getFieldValue }) => {
                  const isBusinessAccountType = getFieldValue('accountType') === UserAccountType.BUSINESS;

                  return isBusinessAccountType ? (
                    <Form.Item
                      label={<span className="text-grey">{t('Company')}</span>}
                      className="desktop:w-1/2"
                      name="company"
                      labelCol={{ style: { paddingBottom: 0 } }}
                      rules={[
                        { required: true, message: t('Please enter company!') },
                        {
                          min: 1,
                          max: 100,
                          message: t('Company name must have 1 - 100 characters!'),
                        },
                      ]}
                    >
                      <Input placeholder={t('Enter company')} bordered size="small" />
                    </Form.Item>
                  ) : (
                    <div className="w-1/2" />
                  );
                }}
              </Form.Item>
            </div>

            <div className="desktop:flex items-center desktop:space-x-7">
              <Form.Item noStyle shouldUpdate={(prevValues, nextValues) => prevValues.accountType !== nextValues.accountType}>
                {({ getFieldValue }) => {
                  const isBusinessAccountType = getFieldValue('accountType') === UserAccountType.BUSINESS;

                  const identificationNumberLabel = t(
                    isBusinessAccountType ? 'Business identification number' : 'Personal identification number',
                  );

                  return (
                    <Form.Item
                      labelCol={{ style: { paddingBottom: 0 } }}
                      className="desktop:w-1/2"
                      name="identificationNumber"
                      label={<span className="text-grey">{identificationNumberLabel}</span>}
                    >
                      <Input bordered placeholder={identificationNumberLabel} size="small" />
                    </Form.Item>
                  );
                }}
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prevValues, nextValues) => prevValues.accountType !== nextValues.accountType}>
                {({ getFieldValue }) => {
                  const isBusinessAccountType = getFieldValue('accountType') === UserAccountType.BUSINESS;

                  const identificationNumberLabel = t(isBusinessAccountType ? 'Signing identificator' : 'Issuing authority');

                  return (
                    <Form.Item
                      labelCol={{ style: { paddingBottom: 0 } }}
                      className="desktop:w-1/2"
                      name={isBusinessAccountType ? 'signingIdentificator' : 'identificationDocIssuingAuthority'}
                      label={<span className="text-grey">{identificationNumberLabel}</span>}
                    >
                      <Input bordered placeholder={identificationNumberLabel} size="small" />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </div>

            {!isBusinessAccountType && (
              <>
                <div className="desktop:flex items-center desktop:space-x-7">
                  <Form.Item
                    labelCol={{ style: { paddingBottom: 0 } }}
                    className="w-full"
                    name="identificationDocIssuingDate"
                    label={<span className="text-grey">{t('Date of issue')}</span>}
                  >
                    <DatePicker bordered className="h-[52px] w-full" format={CLIENT_DATE_FORMAT} size="small" />
                  </Form.Item>
                  <Form.Item
                    labelCol={{ style: { paddingBottom: 0 } }}
                    className="w-full"
                    name="dateOfBirth"
                    label={<span className="text-grey">{t('Date of Birth')}</span>}
                  >
                    <DatePicker
                      bordered
                      className="h-[52px] w-full"
                      format={CLIENT_DATE_FORMAT}
                      disabledDate={disableFutureDatesUnder18}
                      defaultPickerValue={datePickerValueUnder18}
                      size="small"
                    />
                  </Form.Item>
                </div>
                <div className="desktop:flex items-center desktop:space-x-7">
                  <Form.Item
                    labelCol={{ style: { paddingBottom: 0 } }}
                    className="w-full"
                    name="lastName"
                    label={<span className="text-grey">{t('Last Name')}</span>}
                    rules={[{ required: true, message: t('Please enter your last name!') }]}
                  >
                    <Input bordered placeholder={t('Enter last name')} size="small" />
                  </Form.Item>
                  <Form.Item
                    labelCol={{ style: { paddingBottom: 0 } }}
                    className="desktop:w-full"
                    name="firstName"
                    label={<span className="text-grey">{t('Name')}</span>}
                    rules={[{ required: true, message: t('Please enter your first name!') }]}
                  >
                    <Input bordered placeholder={t('Enter first name')} size="small" />
                  </Form.Item>
                  <Form.Item
                    labelCol={{ style: { paddingBottom: 0 } }}
                    className="w-full"
                    name="middleName"
                    label={<span className="text-grey">{t('Middle Name')}</span>}
                  >
                    <Input bordered placeholder={t('Enter middle name')} size="small" />
                  </Form.Item>
                </div>

                <Form.Item
                  labelCol={{ style: { paddingBottom: 0 } }}
                  className="w-full"
                  name="residenceRegistration"
                  label={<span className="text-grey">{t('Residence registration')}</span>}
                >
                  <TextArea bordered placeholder={t('Residence registration')} rows={2} />
                </Form.Item>
              </>
            )}

            <div className="desktop:flex items-center desktop:space-x-7">
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="email"
                label={<span className="text-grey">{t('Email')}</span>}
                rules={[
                  { required: true, message: t('Please enter your email!') },
                  { type: 'email', message: t('Please enter correct email!') },
                ]}
              >
                <Input bordered placeholder={t('Enter email')} size="small" />
              </Form.Item>
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="phone"
                label={<span className="text-grey">{t('Phone')}</span>}
                rules={[
                  { required: true, message: t('Please enter your phone!') },
                  { pattern: PHONE_NUMBER_REGEX, message: t('Please provide a valid phone number. Example: +123456789012345') },
                ]}
              >
                <PhoneInput placeholder={t('Enter phone')} bordered initialValue={phone} size="small" />
              </Form.Item>
            </div>

            <div className="desktop:flex justify-end gap-4">
              <Button theme="secondary" onClick={onClose} containerClassName="w-full mb-4">
                {t('Cancel', { ns: 'common' })}
              </Button>

              <UpdateUser
                userId={userId}
                formInstance={form}
                onSuccess={onClose}
                action={<Button containerClassName="w-full">{t('Save', { ns: 'common' })}</Button>}
              />
            </div>
          </Form>
        </div>
      </div>
    </Drawer>
  );
};
